'use strict';

const ALL_ORGS_LOCAL_STORAGE_KEY = 'Organizations-All';
const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const SELECTED_DEVICE_LOCAL_STORAGE_KEY = 'Selected-Device';
const SELECTED_LOCATION_LOCAL_STORAGE_KEY = 'Selected-Location';
const DEFAULT_BRAND = 'default';

export default angular
  .module('main.devices.edit.dataProvider', [])
  .factory('EditDeviceDataProvider', function (
    $q,
    $state,
    $rootScope,
    $filter,
    $timeout,
    $interval,
    $log,
    utils,
    UsersService,
    Resource,
    LocationsService,
    PermissionsService,
    BotsService,
    DevicesService,
    RulesService,
    config,
    I18nService,
    FilesService,
    ToastsService,
    FirmwareService
  ) {
    const $t = $filter('translate');
    const GATEWAY_DEVICE_CATEGORIES = config.getConfigByKey('gatewayDeviceCategories');
    const ZONE_DEVICE_CATEGORIES = config.getConfigByKey('zoneDeviceCategories');
    const RADAR_DEVICE_CATEGORY = config.getConfigByKey('radarDeviceCategory');
    const NETWORK_TYPES = config.getConfigByKey('networkTypes');

    class EditDeviceDataProvider {

      // #region Public fields

      // #region Resources

      /**
       * Available organizations.
       * @type {Resource}
       */
      availableOrganizationsResource = new Resource(
        () => {
          // TODO move get all orgs stuff to the OrganizationService
          return utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY) || [];
        }
      );

      /**
       * Current organization.
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(
        () => {
          // TODO move get/set current org stuff to the OrganizationService
          return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Child organizations related to the current organization.
       * @type {Resource}
       */
      childOrganizationsResource = new Resource(() => {
        let currentOrg = this.currentOrganizationResource.value;
        if (currentOrg.parentId) {
          return [];
        } else {
          return utils.getChildOrganizations(this.availableOrganizationsResource.value, currentOrg.id);
        }
      }, [
        this.currentOrganizationResource,
        this.availableOrganizationsResource
      ]);

      /**
       * Current user (admin currently logged in).
       * @type {Resource}
       */
      currentUserResource = new Resource(
        () => {
          return UsersService.getCurrentUserInfo();
        }
      );

      /**
       * Current device ID.
       * @type {Resource}
       */
      deviceIdResource = new Resource(
        () => {
          // TODO REMOVE LOCAL STORAGE THING
          return $state.params.deviceId || $state.params.device?.id || utils.getStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY)?.id;
        }
      );

      /**
       * Location ID of the device.
       * @type {Resource}
       */
      deviceLocationIdResource = new Resource(
        () => {
          let lsDevice = utils.getStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY);
          // NOTE device.location is synthetic field
          return $state.params.locationId || lsDevice?.location?.id || lsDevice?.locationId;
        }
      );

      /**
       * All location users
       * @type {Resource}
       */
      locationUsersResource = new Resource(
        () => {
          return LocationsService.getLocationUsers(this.deviceLocationIdResource.value)
            .then(data => data?.users || []);
        },
        [
          this.deviceLocationIdResource
        ]
      );

      /**
       * User id used to retrieve or update device data.
       * @type {Resource}
       */
      userIdResource = new Resource(
        () => {
          return $state.params?.userId || undefined;
        }
      );

      /**
       * Device info (device and location) resource.
       * @type {Resource}
       */
      deviceInfoResource = new Resource(() => {
          return DevicesService.getDevice(this.deviceIdResource.value, this.deviceLocationIdResource.value, true);
        },
        [
          this.deviceIdResource,
          this.deviceLocationIdResource,
        ]
      );

      /**
       * Device resource.
       * @type {Resource}
       */
      deviceResource = new Resource(() => {
          const device = this.deviceInfoResource.value.device;
          utils.setStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY, device);
          this.isDeviceConnected = device.connected;
          return device;
        },
        [
          this.deviceInfoResource,
        ],
        $state.params.device || utils.getStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY)
      );

      /**
       * Device location resource.
       * @type {Resource}
       */
      deviceLocationResource = new Resource(() => {
          return this.deviceInfoResource.value?.location;
        },
        [
          this.deviceInfoResource
        ]
      );

      /**
       * Organization of current device location.
       * @type {Resource}
       */
      deviceLocationOrganizationResource = new Resource(() => {
        return this.availableOrganizationsResource.value.find(o => o.id === this.deviceLocationResource.value.organizationId);
      }, [
        this.deviceLocationResource,
        this.availableOrganizationsResource
      ]);

      /**
       * Brand of organization of current device location.
       * Note: Could be undefined
       * @type {Resource}
       */
      deviceLocationOrganizationBrandResource = new Resource(() => {
        return utils.getBrandName(this.deviceLocationResource.value.organizationId, this.availableOrganizationsResource.value);
      }, [
        this.deviceLocationResource,
        this.availableOrganizationsResource
      ]);

      /**
       * Brand of current selected organization.
       * @type {Resource}
       */
      currentOrganizationBrandResource = new Resource(() => {
        return utils.getBrandName(this.currentOrganizationResource.value.id, this.availableOrganizationsResource.value);
      }, [
        this.currentOrganizationResource,
        this.availableOrganizationsResource
      ]);

      /**
       * Device types resource.
       * @type {Resource}
       */
      deviceTypesResource = new Resource(() => {
        return DevicesService.getDeviceTypes().then(data => data?.deviceTypes || []);
      });

      /**
       * Device type of current device.
       * Note that deviceType could be undefined for some devices.
       * @type {Resource}
       */
      deviceTypeResource = new Resource(() => {
        if (!this.deviceResource.value.type) {
          $log.warn(`Device (id: "${this.deviceResource.value.id}") has no device type!`);
          return;
        }
        let deviceType = this.deviceTypesResource.value.find(dt => dt.id === this.deviceResource.value.type);
        if (!deviceType) {
          $log.warn(`Device (id: "${this.deviceResource.value.id}") has unknown device type "${this.deviceResource.value.type}"`);
          return;
        }
        return deviceType;
      }, [
        this.deviceTypesResource,
        this.deviceResource
      ]);

      /**
       * Device model category.
       * @type {Resource}
       */
      deviceModelCategoryResource = new Resource(() => {
        if (!this.deviceResource.value.modelId) {
          $log.warn('Device has no Model ID!');
          return;
        }

        const brandName = this.deviceLocationOrganizationBrandResource.value;
        const locale = I18nService.getLocale();
        const deviceModelId = this.deviceResource.value.modelId;

        return DevicesService.getDeviceModels({
          modelId: deviceModelId,
          brand: brandName || DEFAULT_BRAND,
          lang: locale.code
        })
          .then((data) => data?.categories?.find(deviceModelCategory =>
            deviceModelCategory.models?.some(deviceModel => deviceModel.id === deviceModelId)
          ));
      }, [
        this.deviceResource,
        this.availableOrganizationsResource,
        this.deviceLocationOrganizationBrandResource
      ]);

      /**
       * Device model.
       * @type {Resource}
       */
      deviceModelResource = new Resource(() => {
        const deviceModelId = this.deviceResource.value.modelId;
        return this.deviceModelCategoryResource.value?.models?.find(deviceModel => deviceModel.id === deviceModelId);
      }, [
        this.deviceModelCategoryResource,
        this.deviceResource
      ]);

      deviceModelsResource = new Resource(() => {
        const locale = I18nService.getLocale();
        return DevicesService.getDeviceModels({
          brand: this.deviceLocationOrganizationBrandResource.value,
          lang: locale.code,
        }).then(data => {
          const categories = data?.categories || [];
          const models = categories.reduce((acc, category) => {
            return acc.concat(category.models);
          }, []);

          // Remove duplicates and empty models
          return models.filter((model, index, self) => {
            return model && self.findIndex(m => m.id === model.id) === index;
          });
        });
      }, [
        this.deviceLocationOrganizationBrandResource,
      ]);

      /**
       * Device rules.
       * @type {Resource}
       */
      deviceRulesResource = new Resource(() => {
        return RulesService.getDeviceRules(this.deviceLocationIdResource.value, this.deviceIdResource.value)
          .then(data => data?.rules || []);
      }, [
        this.deviceLocationIdResource,
        this.deviceIdResource
      ]);

      /**
       * Device files.
       * @type {Resource}
       */
      deviceFilesResource = new Resource(() => {
        return FilesService.getApplicationFiles({
          deviceId: this.deviceIdResource.value,
          locationId: this.deviceLocationIdResource.value,
          type: 3 // device image
        })
          .then((data) => {
            let files = data?.files || [];

            // Get download URLs for every file
            let promises = files.map(file =>
              FilesService.getApplicationFileUrl(file.id, {
                locationId: this.deviceLocationIdResource.value
              })
                .then(data => file.url = data?.contentUrl)
            );

            return $q.all(promises)
              .then(() => files);
          });
      }, [
        this.deviceIdResource,
        this.deviceLocationIdResource
      ]);

      /**
       * Device location bot summary.
       * @type {Resource}
       */
      deviceLocationBotSummaryResource = new Resource(() => {
        return BotsService.getBotSummary(this.deviceLocationIdResource.value);
      }, [
        this.deviceLocationIdResource
      ]);

      /**
       * If goals is driven by bots.
       * @type {Resource}
       */
      isBotDrivenGoalsResource = new Resource(() => {
        return !!this.deviceLocationBotSummaryResource.value?.microServices?.some(ms => ms === 'rules_engine' || ms === 'free_rules_engine');
      }, [
        this.deviceLocationBotSummaryResource
      ]);

      /**
       * Device behaviors (can be driven by bots).
       * @type {Resource}
       */
      deviceGoalsResource = new Resource(() => {
        const stateName = 'behaviors';
        let isBotDrivenGoals = this.isBotDrivenGoalsResource.value;
        let brand = this.deviceLocationOrganizationBrandResource.value;
        let getGoalsPromise;

        if (isBotDrivenGoals) {
          this.#subscribeForLocationStateChanges(stateName, (locationState) => {
            this.deviceGoalsResource.value = locationState?.[this.deviceResource.value.type] || [];
            this.deviceGoalsResource.forceReInitDependentsRecursively();
          });
          return this.#getLocationState(stateName)
            .then(data => data?.value?.[this.deviceResource.value.type] || []);
        } else {
          getGoalsPromise = DevicesService
            .getDeviceGoals(this.deviceResource.value.type, brand)
            .then((data) => data?.goals || []);
        }

        return getGoalsPromise.then((goals) => {
          // If device goal forced to change
          if ($state.params && ($state.params.goalId || $state.params.goalId === 0) && goals.length > 0) {
            let goalId = parseInt($state.params.goalId);
            let selectedGoal = goals.find(goal => goal.id === goalId);
            if (selectedGoal) {
              return this.setCurrentDeviceGoal(selectedGoal)
                .then(() => {
                  return goals;
                });
            }
          }
          return goals;
        });
      }, [
        this.isBotDrivenGoalsResource,
        this.deviceResource,
        this.deviceLocationIdResource
      ]);

      /**
       * Get device network connectivity.
       * @type {Resource}
       */
      deviceNetworkResource = new Resource(() => {
        let deviceTypeAttributes = this.deviceTypeResource.value?.attributes || [];
        let networkType = deviceTypeAttributes.find(el => el.name === 'netType');
        if (!networkType) {
          return;
        }

        // Comma-separated string of network types.
        let networkTypes = networkType.value
          .split(',')
          .reduce((result, item, index) => {
            let network = NETWORK_TYPES.find(el => el.id === parseInt(item));
            if (network) {
              result[network.name] = {
                id: network.id,
                default: index === 0
              };
              return result;
            }
          }, {});

        // Check for at least one supported network type
        if (!networkTypes || Object.keys(networkTypes).length === 0) {
          return;
        }

        return networkTypes;
      }, [
        this.deviceTypeResource
      ]);

      /**
       * Get device proxy Gateway if exist.
       * @type {Resource}
       */
      deviceProxyResource = new Resource(() => {
        let deviceProxyId = this.deviceResource.value.proxyId;
        // If device has proxy
        if (deviceProxyId) {
          return DevicesService.getDevice(deviceProxyId, this.deviceLocationIdResource.value, true)
            .then((data) => data?.device);
        }
      }, [
        this.deviceResource,
        this.deviceLocationIdResource
      ]);

      /**
       * Get device Zone (virtual proxy) if exist.
       * A device may reference its virtual zone device via `zoneId`.
       * @type {Resource}
       */
      deviceZoneResource = new Resource(() => {
        let deviceZoneId = this.deviceResource.value.zoneId;
        // If device belongs to a zone
        if (deviceZoneId) {
          return DevicesService.getDevice(deviceZoneId, this.deviceLocationIdResource.value, true)
            .then((data) => data?.device);
        }
      }, [
        this.deviceResource,
        this.deviceLocationIdResource
      ]);

      /**
       * Gateway device has a list of connected devices.
       * @type {Resource}
       */
      isGatewayResource = new Resource(() => {
        let typeCategory = this.deviceResource.value.typeCategory;
        return GATEWAY_DEVICE_CATEGORIES.some(cat => cat === typeCategory);
      }, [
        this.deviceResource
      ]);

      /**
       * Zone device is a purely virtual proxy device.
       * @type {Resource}
       */
      isZoneResource = new Resource(() => {
        let typeCategory = this.deviceResource.value.typeCategory;
        return ZONE_DEVICE_CATEGORIES.some(cat => cat === typeCategory);
      }, [
        this.deviceResource
      ]);

      /**
       * List of child devices connected to the current gateway or zone device.
       * Children reference the current device either via `proxyId` (gateway)
       * or via `zoneId` (zone).
       * @type {Resource}
       */
      gatewayDevicesResource = new Resource(() => {
        if (!this.deviceLocationIdResource.value) {
          return $q.resolve([]);
        }

        let checkPersistent = true;
        let getTags = true;
        let getProspects = true;

        return DevicesService.getLocationDevices(this.deviceLocationIdResource.value, checkPersistent, getTags, getProspects)
          .then((data) => {
            let devices = data?.devices || [];
            let deviceId = this.deviceResource.value?.id;
            return devices.filter(device => device.proxyId === deviceId || device.zoneId === deviceId);
          });
      }, [
        this.isGatewayResource,
        this.isZoneResource,
        this.deviceResource,
        this.deviceLocationIdResource
      ]);

      isSimulatedDeviceResource = new Resource(() => {
        const device = this.deviceResource.value;
        return device.simulated != null && device.simulated !== -1 && device.simulated !== 0;
      }, [
        this.deviceResource
      ]);

      /**
       * If current device is allowed to pair devices.
       * @type {Resource}
       */
      canPairDeviceResource = new Resource(() => {
        // NOTE: Add additional security checks here
        return !!this.isGatewayResource.value && !this.isSimulatedDeviceResource.value;
      }, [
        this.isGatewayResource,
        this.isSimulatedDeviceResource
      ]);

      /**
       * Radar devices have boundaries and sub-regions.
       * @type {Resource}
       */
      isRadarResource = new Resource(() => {
        let categoryRadar = this.deviceResource.value.typeCategory;
        return RADAR_DEVICE_CATEGORY === categoryRadar;
      }, [
        this.deviceResource
      ]);

      /**
       * Boundaries and sub-regions for the Radar are bot-driven only.
       * Returns '' - no micro-services found, 'radar' - radar micro-service, 'vayyar' - vayyar micro-service
       * @type {Resource}
       * @returns {string}
       */
      botDrivenRegionsResource = new Resource(() => {
        const microServices = this.deviceLocationBotSummaryResource.value?.microServices || [];
        if (microServices.includes('radar')) return 'radar';
        if (microServices.includes('vayyar')) return 'vayyar';
        return '';
      }, [
        this.isRadarResource,
        this.deviceLocationBotSummaryResource
      ]);

      /**
       * Radar room boundaries for current device.
       * @type {Resource}
       */
      locationStateBoundariesResource = new Resource(() => {
        if (!this.botDrivenRegionsResource.value) {
          return $q.resolve({});
        }

        const stateName = this.botDrivenRegionsResource.value + '_room';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.locationStateBoundariesResource.value = locationState?.[this.deviceIdResource.value] || {};
          this.locationStateBoundariesResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value[this.deviceIdResource.value] || {});
      }, [
        this.botDrivenRegionsResource,
        this.deviceLocationIdResource,
        this.deviceIdResource
      ]);

      /**
       * Radar sub-regions for current device.
       * @type {Resource}
       */
      locationStateRegionsResource = new Resource(() => {
        if (!this.botDrivenRegionsResource.value) {
          return $q.resolve([]);
        }

        const stateName = this.botDrivenRegionsResource.value + '_subregions';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.locationStateRegionsResource.value = locationState?.[this.deviceIdResource.value] || [];
          this.locationStateRegionsResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value ? data.value[this.deviceIdResource.value] : []);
      }, [
        this.botDrivenRegionsResource,
        this.deviceLocationIdResource,
        this.deviceIdResource,
      ]);

      /**
       * Radar sub-region behaviors.
       * @type {Resource}
       */
      locationStateRegionBehaviorsResource = new Resource(() => {
        if (!this.botDrivenRegionsResource.value) {
          return $q.resolve([]);
        }

        const stateName = this.botDrivenRegionsResource.value + '_subregion_behaviors';
        this.#subscribeForLocationStateChanges(stateName, (locationState) => {
          this.locationStateRegionBehaviorsResource.value = locationState || [];
          this.locationStateRegionBehaviorsResource.forceReInitDependentsRecursively();
        });
        return this.#getLocationState(stateName)
          .then(data => data?.value || []);
      }, [
        this.botDrivenRegionsResource,
        this.deviceLocationIdResource
      ]);

      /**
       * Current user preferred System of measurement
       * @type {Resource}
       */
      userSystemOfMeasurementResource = new Resource(
        () => {
          return UsersService.getSystemOfMeasurement();
        }
      );

      /**
       * Current device measurements (parameters).
       * @type {Resource}
       */
      deviceCurrentMeasurementsResource = new Resource(() => {
        try {
          this.#subscribeForDeviceMeasurementChanges(
            this.deviceLocationIdResource.value,
            this.deviceIdResource.value,
            (deviceMeasurementChange) => {
              if (!deviceMeasurementChange || !deviceMeasurementChange.params) {
                return;
              }

              const deviceMeasurements = angular.copy(this.deviceCurrentMeasurementsResource.value || {});
              const params = deviceMeasurements.parameters || [];

              deviceMeasurementChange.params.forEach(paramChange => {
                if (!paramChange || paramChange.deviceId !== this.deviceIdResource.value) {
                  return;
                }

                const existingParam = params.find(param => {
                  if (!!paramChange.index || paramChange.index === 0) {
                    return paramChange.name === param.name && paramChange.index === param.index;
                  }
                  return paramChange.name === param.name;
                });

                const updatedParam = {
                  ...existingParam,
                  name: paramChange.name,
                  value: paramChange.value,
                  lastUpdateTimeMs: paramChange.time,
                  lastUpdateTime: new Date(paramChange.time).toISOString()
                };

                if (!existingParam) {
                  params.push(updatedParam);
                } else {
                  params[params.indexOf(existingParam)] = updatedParam;
                }
              });

              deviceMeasurements.parameters = params;
              this.deviceCurrentMeasurementsResource.value = deviceMeasurements;
              this.deviceCurrentMeasurementsResource.forceReInitDependentsRecursively();
            }
          );
        } catch (err) {
          $log.error(`Unable to init device measurements WS subscription`);
          $log.error(err);
        }

        return DevicesService.getDeviceCurrentMeasurements(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          this.userIdResource.value
        )
          .then(data => {
            return data?.devices?.[0];
          });
      }, [
        this.deviceIdResource,
        this.deviceLocationIdResource,
        this.userIdResource,
        this.canPairDeviceResource
      ]);

      /**
       * Is current device in pairing mode
       * @type {Resource}
       */
      isDeviceInPairingModeResource = new Resource(() => {
        let permitJoiningParam = this.deviceCurrentMeasurementsResource.value?.parameters?.find(p => p.name === 'permitJoining');
        return permitJoiningParam && permitJoiningParam.value !== '0' && permitJoiningParam.value !== 0;
        // permitJoining param possible values:
        // 0 - Disabled/Stop
        // 1 - Enabled/Start
        // 2 - Device is currently adding
      }, [
        this.deviceCurrentMeasurementsResource,
      ]);

      /**
       * Device battery status.
       * @type {Resource}
       */
      deviceBatteryStatusResource = new Resource(() => {
        let param = this.deviceCurrentMeasurementsResource
          .value
          ?.parameters
          ?.find(p => p.name === 'batteryLevel');
        return param != null ? parseInt(param.value) : undefined;
      }, [
        this.deviceCurrentMeasurementsResource
      ]);

      /**
       * Device power status.
       * 1 - on
       * 0 - off
       * @type {Resource}
       */
      devicePowerStatusResource = new Resource(() => {
        let param = this.deviceCurrentMeasurementsResource
          .value
          ?.parameters
          ?.find(p => p.name === 'powerStatus');
        return param != null ? parseInt(param.value) : undefined;
      }, [
        this.deviceCurrentMeasurementsResource
      ]);

      /**
       * Device parameters.
       * @type {Resource}
       */
      deviceParametersResource = new Resource(() => {
        return DevicesService.getDeviceParameters()
          .then((data) => data?.deviceParams || []);
      }, []);

      /**
       * Is device currently in pairing mode.
       * @type {Resource}
       */
      permitJoiningResource = new Resource(() => {
        return DevicesService.getDeviceCurrentMeasurements(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          this.userIdResource.value,
          'permitJoining')
          .then(() => {

          });
      }, [
        this.deviceIdResource,
        this.deviceLocationIdResource,
        this.userIdResource
      ]);

      /**
       * If device supports hardware reboot command.
       * @type {Resource}
       */
      canHardwareRebootResource = new Resource(() => {
        return !!this.deviceModelResource.value?.displayInfo?.rebootable && !this.isSimulatedDeviceResource.value;
      }, [
        this.deviceModelResource,
        this.isSimulatedDeviceResource,
      ]);

      /**
       * If device supports cloud change command.
       * @type {Resource}
       */
      canChangeCloudResource = new Resource(() => {
        return this.deviceModelResource.value?.displayInfo?.changeCloud && !this.isSimulatedDeviceResource.value;
      }, [
        this.deviceModelResource,
        this.isSimulatedDeviceResource,
      ]);

      /**
       * If current device always connected.
       * @type {Resource}
       */
      alwaysConnectedResource = new Resource(() => {
        return !!this.deviceModelResource.value?.displayInfo?.hideConnection;
      }, [
        this.deviceModelResource,
      ]);

      /**
       * If current device supports multiple location spaces.
       * @type {Resource}
       */
      deviceSupportMultipleLocationSpacesResource = new Resource(() => {
        return !!this.deviceTypeResource.value
          ?.attributes
          ?.some(el => el.name === 'multipleSpaces' && el.value === '1');
      }, [
        this.deviceTypeResource
      ]);

      /**
       * If device supports user mapping.
       * @type {Resource}
       */
      deviceUserMappingResource = new Resource(() => {
        return this.deviceModelResource.value?.displayInfo?.userMapping;
      }, [
        this.deviceModelResource,
      ]);

      /**
       * Device location spaces.
       * @type {Resource}
       */
      deviceLocationSpacesResource = new Resource(() => {
        const deviceModel = this.deviceModelResource.value;
        const device = this.deviceResource.value;
        const locationId = this.deviceLocationIdResource.value;

        let modelSpaces = deviceModel?.displayInfo?.locationSpaces || [];
        let currentSpaces = device?.spaces || [];

        if (modelSpaces?.length === 0) {
          return currentSpaces.map((el, i) => {
            el.index = i;
            return el;
          });
        }

        return LocationsService.getLocationSpaces(locationId)
          .then(data => {
            let locationSpaces = data?.spaces || [];
            utils.mergeByProperty(modelSpaces, locationSpaces, ['type', 'name']);

            $scope.locationSpaces = modelSpaces.map((el, i) => {
              el.index = i;
              return el;
            });

            $scope.selectedSpaces = currentSpaces.map(el => el = modelSpaces.findIndex(val => val.id === el.id));
            let index = modelSpaces.findIndex(el => currentSpaces[0] && el.id === currentSpaces[0].id);
            $scope.selectedSpace = index === -1 ? null : index;
            initLocationSpaces();
          }, err => {
            $scope.locationSpaces = modelSpaces;
            initLocationSpaces();
            utils.handleApiError(err);
          });
      }, [
        this.deviceSupportMultipleLocationSpacesResource,
        this.deviceLocationIdResource,
        this.deviceModelResource,
        this.deviceResource
      ]);

      deviceModelSpacesResource = new Resource(() => {
        return this.deviceModelResource.value?.displayInfo?.locationSpaces || [];
      }, [
        this.deviceModelResource,
      ]);

      deviceSpacesResource = new Resource(() => {
        return this.deviceResource.value?.spaces || [];
      }, [
        this.deviceResource,
      ]);

      locationSpacesResource = new Resource(() => {
        return LocationsService.getLocationSpaces(this.deviceLocationIdResource.value)
          .then(data => data?.spaces || []);
      }, [
        this.deviceLocationIdResource
      ]);

      /**
       * Resource for checking if current user is allowed to edit current device.
       * @type {Resource}
       */
      canIEditDeviceResource = new Resource(
        () => {
          return PermissionsService.canIEditDevice(this.deviceResource.value);
        }, [
          this.deviceResource
        ]
      );

      /**
       * Resource for checking if current user is allowed to delete current device.
       * @type {Resource}
       */
      canIDeleteDeviceResource = new Resource(
        () => {
          return PermissionsService.canIDeleteDevice(this.deviceResource.value);
        }, [
          this.deviceResource
        ]
      );

      /**
       * Organization FW update groups resource.
       * @type {Resource}
       */
      firmwareGroupsResource = new Resource(
        () => {
          return FirmwareService.getFirmwareGroups(this.deviceLocationResource.value.organizationId);
        }, [
          this.deviceLocationResource,
        ]
      );

      /**
       * Device type FW update jobs.
       * @type {Resource}
       */
      firmwareUpdateJobsResource = new Resource(
        () => {
          const device = this.deviceResource.value;
          let deviceType = device?.type;
          let firmwareGroupId = device?.fwGroupId;

          return FirmwareService.getFirmwareUpdateJobs(deviceType, firmwareGroupId);
        }, [
          this.deviceResource,
        ]
      );

      /**
       * Firmware versions resource.
       * @type {Resource}
       */
      firmwareVersionsResource = new Resource(
        () => {
          const device = this.deviceResource.value;
          let deviceType = device?.type;

          return FirmwareService.getFirmwareVersions(deviceType);
        }, [
          this.deviceResource,
        ]
      );

      /**
       * Device simCards resource.
       * @type {Resource}
       */
      simCardsResource = new Resource(
        () => {
          this.#subscribeForDeviceSimCardsChanges(
            this.deviceLocationIdResource.value,
            this.deviceIdResource.value,
            (simCardsChange) => {
              if (!simCardsChange || !simCardsChange.simCard) {
                return;
              }

              const simCards = angular.copy(this.deviceResource.value?.simCards || []);
              const newSimCard = simCardsChange.simCard;

              const ind = simCards.findIndex(sc => sc.id === newSimCard.id);
              if (ind !== -1) {
                simCards[ind] = newSimCard;
              } else {
                simCards.push(simCards[ind]);
              }

              const device = angular.copy(this.deviceResource?.value);
              device.simCards = simCards;
              this.deviceResource.value = device;
              this.simCardsResource.value = simCards;
              this.deviceResource.forceReInitDependentsRecursively();
            }
          );
          return this.deviceResource?.value?.simCards || [];
        },
        [
          this.deviceResource,
          this.deviceLocationIdResource,
          this.deviceIdResource
        ]
      );

      // #endregion

      /**
       * Current (active) tab.
       * @type {'summary'|'edit'|'subscriptions'|'users'|'schedules'|'devices'|'events'|'services'|'scenes'|'bots'|'rules'|'settings'|'states'|'tasks'|'priorities'|'cellular'|'regions'|undefined}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab.
       * @return {'summary'|'edit'|'subscriptions'|'users'|'schedules'|'devices'|'events'|'services'|'scenes'|'bots'|'rules'|'settings'|'states'|'tasks'|'priorities'|'cellular'|'regions'|undefined}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs.
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      isUnlimitedAdmin = PermissionsService.isUnlimitedAdmin();
      isPaidAdmin = PermissionsService.isPaidAdmin();
      isServiceAdmin = PermissionsService.isServiceAdmin();

      isDeviceConnected = false;
      /**
       * 'Forced' pairing mode.
       * This mode enables for some amount of time after enabling pairing mode to disable pairing buttons and actions.
       * @type {boolean}
       */
      isDeviceInForcedPairingMode = false;
      isDeviceRebooting = false;

      /**
       * Radar dependencies
       */
      subregionToEdit;

      isFirmwareUpgradable = false;
      isParamsRefreshed = false;
      refreshableParams = [];

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      // #region Small hack to avoid multiple calls to single getLocationState API

      /**
       * Queue for debounced Get Location State function
       * @type {Array<{stateName: string, deferred: Deferred}>}
       */
      #getLocationStatesQueue = [];

      /**
       * WebSocket subscriptions
       */
      #wsSubscriptions = {};

      /**
       * Get Location State (debounced function)
       * @param {string} stateName
       * @return {Promise<{value: Object}>}
       */
      #getLocationState(stateName) {
        // debounce and couple requests
        let deferred = $q.defer();
        this.#getLocationStatesQueue.push({ stateName: stateName, deferred: deferred });
        this.#getLocationStateDebounced();
        return deferred.promise;
      }

      /**
       * Subscribe for location state changes
       * @param {string} stateName
       * @param {function} onChange
       */
      #subscribeForLocationStateChanges(stateName, onChange) {
        let subscription;
        if (this.#wsSubscriptions[stateName]) {
          subscription = this.#wsSubscriptions[stateName];
        } else {
          subscription = LocationsService.subscribeForLocationStates(this.deviceLocationIdResource.value, stateName);
        }

        subscription.onCreate((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data?.locationState?.value);
          });
        });

        subscription.onEdit((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data?.locationState?.value);
          });
        });

        subscription.onDelete((data) => {
          $rootScope.$applyAsync(() => {
            onChange(undefined);
          });
        });

        subscription.onError((data) => {
          $log.warn(`Location state '${stateName}' subscription error`);
          $log.warn(data);
        });

        this.#wsSubscriptions[stateName] = subscription;

        return subscription;
      }

      #getLocationStateDebounced = utils.debounce(() => {
        let queue = this.#getLocationStatesQueue;
        this.#getLocationStatesQueue = [];
        let locationStates = queue.map(q => q.stateName)
          .filter((value, index, self) => self.indexOf(value) === index); // remove duplicates

        LocationsService.getLocationState(this.deviceLocationIdResource.value, locationStates)
          .then(data => {
            let states = data?.states || [];
            queue.forEach(q => {
              let state = states.find(s => s.name === q.stateName);
              if (state) {
                q.deferred.resolve({ value: state.value });
              } else {
                if (data?.value) { // if requested only one state
                  q.deferred.resolve({ value: data?.value });
                } else {
                  q.deferred.resolve({ value: undefined });
                }
              }
            });
          }, (err) => {
            queue.forEach(q => {
              q.deferred.reject(err);
            });
          });
      }, 50);

      /**
       * Subscribe for device measurements (device params) changes
       * @param {number} locationId
       * @param {string} deviceId
       * @param {function} onChange
       */
      #subscribeForDeviceMeasurementChanges(locationId, deviceId, onChange) {
        let subscription = this.#wsSubscriptions['device_measurements'];
        if (subscription) {
          return;
        }

        subscription = DevicesService.subscribeForDeviceParameters(locationId, deviceId);
        this.#wsSubscriptions['device_measurements'] = subscription;

        subscription.onCreate((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onEdit((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onDelete((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onError((data) => {
          $log.warn(`Location device measurements subscription error`);
          $log.warn(data);
        });

        return subscription;
      }

      /**
       * Subscribe for SIM cards changes
       * @param {number} locationId
       * @param {string} deviceId
       * @param {function} onChange
       */
      #subscribeForDeviceSimCardsChanges(locationId, deviceId, onChange) {
        let subscription = this.#wsSubscriptions['device_sim_cards'];
        if (subscription) {
          return;
        }

        subscription = DevicesService.subscribeForSimCards(locationId, deviceId);
        this.#wsSubscriptions['device_sim_cards'] = subscription;

        subscription.onCreate((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onEdit((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onDelete((data) => {
          $rootScope.$applyAsync(() => {
            onChange(data);
          });
        });

        subscription.onError((data) => {
          $log.warn(`Location device SIM cards subscription error`);
          $log.warn(data);
        });

        return subscription;
      }

      // #endregion

      // #endregion

      /**
       * Selected device parameter data object.
       */
      currentHistoryParam;

      /**
       * Selected device parameter index.
       */
      currentHistoryParamIndex;

      constructor() {
      }

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button.
       */
      goBack() {
        if ($state.params && $state.params.from) {
          switch ($state.params.from) {
            case 'user':
              $state.go('main.users.edit', {
                tab: 'devices'
              });
              return;
            case 'location':
              $state.go('main.locations.edit', {
                tab: 'devices'
              });
              return;
            case 'notification':
              $state.go('main.notifications.info', {
                tab: 'relations'
              });
              return;
          }
        }
        $state.go('main.devices.list');
      }

      /**
       * Go to device location page.
       * @param {string} tabName
       */
      goToDeviceLocation(tabName) {
        this.deviceLocationResource.init()
          .then(() => {
            if (!PermissionsService.canIEditLocation(this.deviceLocationResource.value)) {
              return;
            }
            utils.setStorageItem(SELECTED_LOCATION_LOCAL_STORAGE_KEY, this.deviceLocationResource.value);
            $state.go('main.locations.edit', {
              locationId: this.deviceLocationResource.value.id,
              location: angular.copy(this.deviceLocationResource.value),
              tab: tabName || 'devices',
              from: 'device'
            });
          });
      }

      goToDevice(device, tabName) {
        if (!PermissionsService.canIEditDevice(device)) {
          return;
        }

        utils.setStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY, device);
        $state.go('main.devices.edit', {
          tab: tabName || undefined,
          deviceId: device.id,
          device: angular.copy(device),
        }, {
          location: true,
          notify: true,
          reload: true
        });
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }

        // Unsubscribe from WebSocket events
        for (const [stateName, subscription] of Object.entries(this.#wsSubscriptions)) {
          subscription.unsubscribe();
        }
      }

      /**
       * Register prospect device.
       * @param {Object} device Device to register
       * @return {Promise}
       */
      registerDevice(device) {
        return DevicesService.registerDevice(void 0, {
          deviceId: device.id,
          locationId: this.deviceLocationIdResource.value,
          proxyId: this.deviceIdResource.value,
        })
          .then(() => {
            ToastsService.success(null, $t('devices.deviceRegistered'));
            this.gatewayDevicesResource.init(true);
          });
      }

      /**
       * Delete device.
       * @return {Promise}
       */
      deleteDevice() {
        return DevicesService.deleteDevice(this.deviceLocationIdResource.value, this.deviceIdResource.value)
          .then(() => {
            ToastsService.success(null, $t('common.delete-device-success'));
            $state.go('main.devices.list');
          });
      }

      /**
       * Delete device rule.
       * @param {number} ruleId
       * @return {Promise}
       */
      deleteDeviceRule(ruleId) {
        return RulesService.deleteRule(ruleId, this.userIdResource.value)
          .then(() => {
            if (this.deviceRulesResource.value) {
              this.deviceRulesResource.value = this.deviceRulesResource.value.filter((r) => r.id !== ruleId);
            }
          });
      }

      /**
       * Add set of default device rules.
       * @return {Promise}
       */
      createDeviceDefaultRules() {
        return RulesService.createDeviceDefaultRules(this.deviceIdResource.value, this.userIdResource.value)
          .then(() => this.deviceRulesResource.init(true));
      }

      setCurrentDeviceGoal(goal) {
        let data = {
          device: {
            goalId: goal.id
          }
        };

        return DevicesService.updateDeviceAtLocation(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          data)

          .then(() => {
            if (this.deviceResource.value) {
              this.deviceResource.value.goalId = goal.id;
              utils.setStorageItem(SELECTED_DEVICE_LOCAL_STORAGE_KEY, this.deviceResource.value);
            } else {
              this.deviceResource.init(true);
            }

            this.deviceRulesResource.init(true);
          });
      }

      rebootDevice() {
        if (this.isDeviceRebooting || this.isDeviceInPairingModeResource.value || this.isDeviceInForcedPairingMode) {
          return;
        }
        this.isDeviceRebooting = true;

        let command = {
          'params': [
            { name: 'reboot', value: '1' }
          ]
        };
        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command)
          .then(() => {
            return this.startTrackingConnection()
              .then(() => {
                this.isDeviceRebooting = false;
              });

            // return new Promise(resolve => {
            //   $timeout(() => {
            //     // Force disable button for 60 seconds
            //     this.isDeviceRebooting = false;
            //     resolve();
            //   }, 60000);
            // });
          }, (err) => {
            this.isDeviceRebooting = false;
            utils.handleApiError(err);
          });
      }

      startPairingMode() {
        if (!this.deviceIdResource.value ||
          !this.deviceLocationIdResource.value ||
          !this.isDeviceConnected ||
          this.isDeviceInForcedPairingMode ||
          this.isDeviceInPairingModeResource.value ||
          this.isDeviceRebooting) {
          return;
        }

        const command = {
          'commandType': 0,
          'commandTimeout': 60,
          'comment': 'Trigger gateway pairing mode',
          'params': [
            { name: 'permitJoining', value: '1' },
            { name: 'scanDuration', value: '60' },
          ]
        };

        this.isDeviceInForcedPairingMode = true;

        $timeout(() => {
          // Force the button to be disabled for 5 seconds, even if the device settings have not changed.
          this.isDeviceInForcedPairingMode = false;
        }, 5000);

        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command
        );
      }

      /**
       * Start monitoring the 'connected' status of the device
       * @returns {*}
       */
      startTrackingConnection() {
        return $q((resolve) => {
          let promiseInAction = false; // avoid doubling requests when server is slow
          let counter = 0;
          const interval = $interval(() => {
            if (promiseInAction) {
              return;
            }
            promiseInAction = true;
            counter++;

            DevicesService.getDevice(
              this.deviceIdResource.value,
              this.deviceLocationIdResource.value,
              true
            )
              .then((data) => {
                promiseInAction = false;
                const connected = data.device?.connected;

                // Update connection value
                this.isDeviceConnected = connected;
                this.deviceInfoResource.value.device.connected = connected;

                // Connection did not drop or back online after 80 seconds
                // or dismiss rebooting state after 180 seconds
                if ((counter >= 8 && connected) || (counter === 18 && !connected)) {
                  $interval.cancel(interval);
                  resolve();
                }
              }, () => {
                promiseInAction = false;
              });
          }, 10000);
        });
      }

      /**
       * Save device information.
       * @param {string} deviceName
       * @param {string} [deviceModelId]
       * @param {number} [userId]
       * @return {Promise}
       */
      saveDeviceInfo(deviceName, deviceModelId, userId) {
        let data = {
          device: {
            desc: deviceName || this.deviceResource.value.desc || undefined,
            modelId: deviceModelId || this.deviceResource.value.modelId || undefined,
          }
        };

        if (userId || userId === 0) {
          data.device.userId = userId;
        }

        return DevicesService.updateDeviceAtLocation(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          data)
          .then(() => {
            this.deviceResource.value.desc = data.device.desc;
            this.deviceResource.value.modelId = data.device.modelId;
            this.deviceResource.value.userId = data.device.userId || undefined;

            return $q.all([
              this.deviceResource.init(true),
              deviceModelId ? this.deviceModelCategoryResource.init(true) : undefined,
              deviceModelId ? this.deviceModelResource.init(true) : undefined
            ]);
          });
      }

      /**
       * Mark device as new.
       * @param {boolean} [isNewDevice]
       * @return {Promise}
       */
      setNewDevice(isNewDevice) {
        let data = {
          device: {
            newDevice: !!isNewDevice
          }
        };

        return DevicesService.updateDeviceAtLocation(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          data
        )
          .then(() => {
            this.deviceResource.value.newDevice = !!isNewDevice;
            return $q.when(this.deviceResource.init(true))
              .then(() => ToastsService.success(null, $t('common.saved-success')));
          });
      }

      /**
       * Change device cloud command.
       * @param {string} host
       * @return {Promise}
       */
      changeCloud(host) {
        let command = {
          'params': [
            { name: 'cloud', value: host }
          ]
        };

        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command);
      }

      /**
       * Search locations in current organization available for current device.
       * @param {string} searchTerm Search by location ID, name, address, zip.
       * @param {boolean} currentOrg To get location only in current organization.
       * @return {Promise}
       */
      searchLocations(searchTerm, currentOrg) {
        let params = {
          limit: 100,
          searchBy: searchTerm + '*'
        };

        let organizationId = currentOrg ? this.currentOrganizationResource.value.id : void 0;

        return LocationsService.getLocations(organizationId, params)
          .then(data => data.locations || []);
      }

      /**
       * Link device to another location.
       * @param location
       * @return {Promise}
       */
      bindDeviceToLocation(location) {
        const newLocationId = location.id;
        const oldLocationId = this.deviceLocationIdResource.value;
        const deviceId = this.deviceIdResource.value;
        return DevicesService.moveDeviceToLocation(deviceId, oldLocationId, newLocationId);
      }

      addLocationSpace(spaceType, spaceName) {
        const locationId = this.deviceLocationIdResource.value;
        return LocationsService.addLocationSpace(locationId, spaceType, spaceName);
      }

      linkSpace(spaceId) {
        const deviceId = this.deviceIdResource.value;
        const locationId = this.deviceLocationIdResource.value;
        return DevicesService.linkSpace(deviceId, locationId, spaceId);
      }

      unlinkSpace(spaceId) {
        const deviceId = this.deviceIdResource.value;
        const locationId = this.deviceLocationIdResource.value;
        return DevicesService.unlinkSpace(deviceId, locationId, spaceId);
      }

      unlinkSpaces(spaces) {
        return $q.all(spaces.map((space) => this.unlinkSpace(space.id)));
      }

      /**
       * Save room boundaries
       * @param {Object} boundaries
       * @return {Promise}
       */
      saveRoomBoundaries(boundaries) {
        const addressName = this.botDrivenRegionsResource.value;
        let payload = boundaries;
        let locationId = parseInt(this.deviceLocationIdResource.value);
        payload['device_id'] = this.deviceIdResource.value;
        return BotsService.sendDataStreamMessage({
            locations: [
              locationId
            ],
            feed: payload
          },
          {
            scope: 1,
            address: 'set_' + addressName + '_room',
            locationId: locationId
          })
          .then((data) => {
            let currentBoundaries = this.locationStateBoundariesResource.value;
            if (data && data.resultCode === 0) {
              this.locationStateBoundariesResource.value = { ...currentBoundaries, ...boundaries };
            }
            return data;
          });
      }

      /**
       * Save Radar subregion data
       * @param {Object} subregion
       * @param {boolean} isNewSubregion
       * @return {Promise}
       */
      saveRadarSubregion(subregion, isNewSubregion) {
        const addressName = this.botDrivenRegionsResource.value;
        let payload = subregion;
        let locationId = parseInt(this.deviceLocationIdResource.value);
        payload['device_id'] = this.deviceIdResource.value;
        return BotsService.sendDataStreamMessage({
            locations: [
              locationId
            ],
            feed: payload
          },
          {
            scope: 1,
            address: 'set_' + addressName + '_subregion',
            locationId: locationId
          })
          .then((data) => {
            if (data && data.resultCode === 0) {
              let currentSubregions = this.locationStateRegionsResource.value || [];

              if (isNewSubregion) {
                currentSubregions.push(subregion);
              } else {
                let i = currentSubregions.findIndex(el => el['unique_id'] === subregion['unique_id']);
                if (i >= 0) {
                  currentSubregions[i] = { ...currentSubregions[i], ...subregion };
                }
              }
              this.locationStateRegionsResource.value = currentSubregions;
            }
            return data;
          });
      }

      /**
       * Delete Radar subregion
       * @param {Object} subregion
       * @return {Promise}
       */
      deleteRadarSubregion(subregion) {
        const addressName = this.botDrivenRegionsResource.value;
        let payload = subregion;
        let locationId = parseInt(this.deviceLocationIdResource.value);
        payload['device_id'] = this.deviceIdResource.value;
        return BotsService.sendDataStreamMessage({
            locations: [
              locationId
            ],
            feed: payload
          },
          {
            scope: 1,
            address: 'delete_' + addressName + '_subregion',
            locationId: locationId
          })
          .then((data) => {
            if (data && data.resultCode === 0) {
              let currentSubregions = this.locationStateRegionBehaviorsResource.value || [];
              let i = currentSubregions.findIndex(el => el['unique_id'] === subregion['unique_id']);
              if (i >= 0) {
                currentSubregions.splice(i, 1);
                this.locationStateRegionBehaviorsResource.value = currentSubregions;
              }
            }
            return data;
          });
      }

      /**
       * Update device FW group.
       * @param {number} [groupId]
       * @return {Promise}
       */
      updateDeviceFirmwareGroup(groupId) {
        return FirmwareService.updateFirmwareGroup(this.deviceIdResource.value, groupId)
          .then(() => {
            this.deviceResource.value.fwGroupId = groupId || undefined;
            return $q.when(this.deviceResource.init(true))
              .then(() => ToastsService.success(null, $t('devices.firmware.groupUpdated')));
          });
      }

      updateDeviceParameter(command) {
        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command)
          .then(() => ToastsService.success(null, $t('devices.measurements.commandSuccess')));
      }

      refreshDeviceParams(params) {
        const command = {
          'commandType': 4,
          'comment': 'Refresh device parameters',
          'params': params
        };
        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command)
          .then(() => ToastsService.success(null, $t('devices.info.refresh.success')));
      }

      updateWiFiNetworksList() {
        const command = {
          commandType: 0,
          commandTimeout: 60,
          // comment: 'Refresh Wi-Fi network list from Maestro',
          params: [
            {
              name: 'wifiSsidValues',
              value: '1'
            }
          ]
        };
        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command
        );
      }

      updateWiFiConnection(ssid, security, password, autoConnect) {
        const command = {
          commandType: 0,
          commandTimeout: 60,
          params: [
            {
              name: 'wifiSsid',
              value: ssid
            },
            {
              name: 'wifiPassword',
              value: password || ''
            },
            {
              name: 'wifiSecurity',
              value: security
            },
            {
              name: 'wifiAutoConnected',
              value: autoConnect ? '1' : '0'
            }
          ]
        };
        return DevicesService.sendCommandToDevice(
          this.deviceIdResource.value,
          this.deviceLocationIdResource.value,
          command
        );
      }

      /**
       * Activate SIM card.
       * @return {Promise}
       */
      activateSimCard(simId) {
        return DevicesService.activateSimCard(this.deviceLocationIdResource.value, this.deviceIdResource.value, simId, true)
          .then(() => {
            ToastsService.success(null, $t('devices.info.cellular.activateStatus.activate'));
          });
      }

      // #endregion

    }

    return EditDeviceDataProvider;
  })
  .name;
